<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Expediente{{ employeeName ? ` de ${employeeName}` : '' }}</h1>
    </div>

    <wz-card title="Generar">
      <p class="text-muted text-sm mb-3">
        El legajo de ingreso se genera en segundo plano, completo o no se genera: si algo falla, no se crea ningún
        documento y el motivo aparece en el tablero de Legajo documental.
      </p>
      <p v-if="!contracts.length" class="mb-0 text-muted">El colaborador no tiene contratos vigentes.</p>
      <div v-for="c in contracts" :key="c.contractId" class="flex flex-wrap items-end gap-2 mb-3">
        <div style="min-width: 180px;">
          <label class="wz-form-label">Contrato</label>
          <div class="py-2"><strong>{{ c.contractNumber }}</strong></div>
        </div>
        <wz-button variant="primary" class="mb-1" @click="generate(c)">Generar legajo de ingreso</wz-button>
        <div class="flex-1" style="min-width: 240px;">
          <label class="wz-form-label">Documento disponible</label>
          <wz-select v-model="choice[c.contractId]" :items="availableOptions(c.contractId)" defaultOption="Elegir" />
        </div>
        <wz-button variant="light" class="mb-1" :disabled="!choice[c.contractId]" @click="generateOne(c)">Generar</wz-button>
      </div>
    </wz-card>

    <wz-table-container class="mt-3">
      <wz-table>
        <thead>
          <tr>
            <th>Documento</th>
            <th>Contrato</th>
            <th>Estado</th>
            <th>Entrega</th>
            <th>Generado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="documents.length === 0">
            <td colspan="6" class="text-center text-muted py-4">Sin documentos en el expediente.</td>
          </tr>
          <template v-for="d in documents">
            <tr :key="d.id">
              <td>
                {{ d.documentTypeName }}
                <div v-if="d.hashMismatchAt" class="text-sm" style="color: var(--admin-danger, #dc3545);">
                  <i class="ri-error-warning-line"></i> Archivo alterado, detectado el {{ formatDate(d.hashMismatchAt) }}
                </div>
                <div v-if="d.voidReason" class="text-sm text-muted">Motivo: {{ d.voidReason }}</div>
              </td>
              <td>{{ d.contractNumber || '—' }}</td>
              <td><wz-badge :variant="statusVariant(d.documentStatus)" pill>{{ d.documentStatus }}</wz-badge></td>
              <td>{{ d.deliveryMode || '—' }}</td>
              <td>{{ formatDate(d.generatedAt) }}</td>
              <td style="text-align: center;">
                <wz-dropdown :items="actions(d)" placement="left" @click="handle(d, $event)" />
              </td>
            </tr>
            <tr v-if="openLog === d.id" :key="d.id + '-log'">
              <td colspan="6">
                <strong>Bitácora</strong>
                <p v-if="d.documentHash" class="text-sm text-muted mb-1">SHA-256: <code>{{ d.documentHash }}</code></p>
                <p v-if="!log.length" class="mb-0 text-muted">Sin eventos.</p>
                <div v-for="e in log" :key="e.id" class="text-sm">
                  {{ formatDate(e.occurredAt, true) }} — <strong>{{ e.eventType }}</strong> — {{ e.actor }}<span v-if="e.channel"> — {{ e.channel }}</span>
                </div>

                <div class="mt-3">
                  <strong>Firmas electrónicas</strong>
                  <wz-badge v-if="signatures.length" :variant="chainValid ? 'success' : 'danger'" pill class="ml-2">{{ chainValid ? 'Cadena válida' : 'Cadena rota' }}</wz-badge>
                </div>
                <p v-if="signatures.length && !chainValid" class="text-sm mb-1" style="color: var(--admin-danger, #dc3545);">
                  Alguna firma no parte de donde quedó la anterior, o el archivo ya no coincide con la última firma.
                </p>
                <p v-if="!signatures.length" class="mb-0 text-sm text-muted">Sin firmas electrónicas.</p>
                <div v-for="s in signatures" :key="s.id" class="flex flex-wrap items-center gap-2 text-sm mt-1">
                  <span>
                    {{ s.signOrder }}. {{ formatDate(s.signedAt, true) }} — <strong>{{ s.signerName }}</strong><span v-if="s.signerDocument"> ({{ s.signerDocument }})</span>
                    — {{ s.signerType }}<span v-if="s.channel"> — código por {{ s.channel }} a {{ s.sentTo }}</span><span v-if="s.ipAddress"> — IP {{ s.ipAddress }}</span>
                  </span>
                  <wz-button variant="light" size="sm" @click="downloadEvidence(s)">Descargar expediente de firma</wz-button>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </wz-table>
    </wz-table-container>

    <wz-card title="Correo validado" class="mt-3">
      <p class="text-muted text-sm mb-2">
        Es para quien perdió su correo: sin esto, sus códigos seguirían llegando al anterior. Corrige primero el correo en su
        ficha. Al correo anterior le llega un aviso y se cierran sus sesiones del portal.
      </p>
      <div class="flex justify-end">
        <wz-button variant="light" @click="revokeOpen = true">Retirar correo validado</wz-button>
      </div>
    </wz-card>

    <wz-modal v-model="revokeOpen" size="sm" title="Retirar correo validado" :close-on-overlay="false">
      <p class="mb-3 text-sm">
        Es para quien perdió su correo. Al correo anterior le llega un aviso, para que se entere si no lo pidió.
      </p>
      <wz-textarea v-model="revokeReason" label="Motivo" :rows="3" required />
      <template #footer>
        <wz-button variant="outline" @click="revokeOpen = false">Cancelar</wz-button>
        <wz-button variant="danger" :disabled="!revokeReason.trim()" @click="revoke">Retirar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert, Util } from '@wizepe/wizeui'
import fileDownload from 'js-file-download'
import { LegajoService, SignatureService } from '@/core/services'

export default {
  name: 'EmployeeLegajo',
  data() {
    return {
      documents: [],
      contracts: [],
      available: {},
      choice: {},
      openLog: null,
      log: [],
      signatures: [],
      chainValid: true,
      revokeOpen: false,
      revokeReason: '',
    }
  },
  computed: {
    employeeId() { return Number(this.$route.params.id) },
    employeeName() { return this.documents.length ? this.documents[0].employeeName : '' },
  },
  async created() {
    await this.load()
    try {
      this.contracts = (await LegajoService.currentContracts(this.employeeId)).payload || []
      await Promise.all(this.contracts.map(async c => {
        this.$set(this.available, c.contractId, (await LegajoService.available(c.contractId)).payload || [])
      }))
    } catch (e) {
      Alert.error(e.errors ? e.errors.message : 'Error al cargar los contratos')
    }
  },
  methods: {
    async load() {
      try {
        this.documents = (await LegajoService.listByEmployee(this.employeeId)).payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el expediente')
      }
    },
    availableOptions(contractId) {
      return (this.available[contractId] || []).map(t => ({ key: t.id, label: t.name }))
    },
    async run(action, success) {
      try {
        await this.$store.dispatch('app/loading', true)
        await action()
        if (success) Alert.success(success)
        await this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo completar la operación')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    generate(c) {
      return this.run(() => LegajoService.generate(c.contractId), 'En cola: se genera en segundo plano. Actualiza en unos momentos.')
    },
    generateOne(c) {
      return this.run(() => LegajoService.generateDocument(c.contractId, this.choice[c.contractId]), 'Documento generado')
    },
    actions(d) {
      const items = [{ key: 'log', label: 'Ver bitácora y firmas' }]
      if (d.fileName) items.push({ key: 'download', label: 'Descargar' })
      if (d.documentHash) items.push({ key: 'verify', label: 'Verificar integridad' })
      if (d.fileName && d.deliveryMode === 'ELECTRONICO' && ['GENERADO', 'ENVIADO'].includes(d.documentStatus)) items.push({ key: 'send', label: 'Avisar por el canal configurado' })
      if (d.fileName && !['ANULADO', 'FIRMADO', 'ENTREGADO'].includes(d.documentStatus)) items.push({ key: 'physical', label: 'Registrar entrega física' })
      if (d.fileName && !['ANULADO', 'FIRMADO'].includes(d.documentStatus)) items.push({ key: 'paper', label: 'Registrar firma en papel' })
      if (d.documentStatus === 'PENDIENTE' && d.origin === 'GENERATED') items.push({ key: 'generate', label: 'Generar: se entrega hoy' })
      if (d.documentStatus !== 'ANULADO') {
        if (d.origin === 'GENERATED' && d.documentStatus !== 'FIRMADO' && d.documentStatus !== 'PENDIENTE') items.push({ key: 'regenerate', label: 'Regenerar' })
        items.push({ key: 'void', label: 'Anular' })
      }
      return items
    },
    handle(d, item) {
      if (item.key === 'log') this.toggleLog(d)
      if (item.key === 'download') this.download(d)
      if (item.key === 'verify') this.verify(d)
      if (item.key === 'send') this.run(() => LegajoService.send(d.id), 'Aviso procesado: revisa la bitácora')
      if (item.key === 'physical') {
        const notes = window.prompt('Dónde o cómo se entregó (opcional)')
        if (notes !== null) this.run(() => LegajoService.physicalDelivery(d.id, notes.trim()), 'Entrega física registrada')
      }
      if (item.key === 'paper') {
        const notes = window.prompt('Dónde está el original firmado (opcional)')
        if (notes !== null) this.run(() => LegajoService.paperSignature(d.id, notes.trim()), 'Firma en papel registrada')
      }
      if (item.key === 'generate') {
        Alert.dialog('Generar',
          `"${d.documentTypeName}" saldrá con la fecha de hoy, porque hoy es cuando se entrega. Si el original existe en papel, lo que corresponde es subir el escaneo. ¿Continuar?`,
          () => this.run(() => LegajoService.generatePending(d.id), 'Documento generado'))
      }
      if (item.key === 'regenerate') {
        Alert.dialog('Regenerar', `Se creará un documento nuevo y "${d.documentTypeName}" actual quedará anulado. ¿Continuar?`,
          () => this.run(() => LegajoService.regenerate(d.id), 'Documento regenerado'))
      }
      if (item.key === 'void') {
        const reason = window.prompt('Motivo de la anulación (obligatorio)')
        if (reason && reason.trim()) this.run(() => LegajoService.voidDocument(d.id, reason.trim()), 'Documento anulado')
      }
    },
    async toggleLog(d) {
      if (this.openLog === d.id) {
        this.openLog = null
        return
      }
      try {
        this.log = (await LegajoService.events(d.id)).payload || []
        this.openLog = d.id
      } catch (e) {
        Alert.error('Error al cargar la bitácora')
        return
      }
      // Aparte: si fallan las firmas, la bitácora se sigue viendo.
      this.signatures = []
      this.chainValid = true
      try {
        const signed = (await SignatureService.documentSignatures(d.id)).payload || {}
        this.signatures = signed.signatures || []
        this.chainValid = signed.chainValid
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las firmas')
      }
    },
    async downloadEvidence(s) {
      try {
        const resp = await SignatureService.evidence(s.id)
        fileDownload(resp.data, Util.getFileName(resp) || `expediente-firma-${s.id}.pdf`)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'El expediente no está disponible')
      }
    },
    async revoke() {
      try {
        await LegajoService.revokeContact(this.employeeId, this.revokeReason.trim())
        this.revokeReason = ''
        Alert.success('Correo validado retirado')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo retirar el correo')
      }
      this.revokeOpen = false
    },
    async download(d) {
      try {
        const resp = await LegajoService.download(d.id)
        fileDownload(resp.data, Util.getFileName(resp) || d.fileName)
      } catch (e) {
        Alert.error('El archivo no está disponible')
      }
    },
    async verify(d) {
      try {
        const r = (await LegajoService.verify(d.id)).payload
        if (r && r.hashMismatchAt) Alert.error('El archivo NO coincide con el hash guardado: quedó marcado como alterado')
        else Alert.success('El archivo coincide con el hash guardado')
        await this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al verificar')
      }
    },
    statusVariant(s) {
      if (s === 'FIRMADO' || s === 'ENTREGADO') return 'success'
      if (s === 'ANULADO' || s === 'VENCIDO') return 'secondary'
      if (s === 'PENDIENTE') return 'warning'
      return 'primary'
    },
    formatDate(d, withTime) {
      if (!d) return '—'
      const s = String(d)
      const parts = s.substring(0, 10).split('-')
      const date = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : s
      return withTime && s.length > 10 ? `${date} ${s.substring(11, 19)}` : date
    },
  },
}
</script>
